import React, { useState, useEffect } from 'react';
import {
  Typography,
  Container,
  Box,
  Grid,
  Card,
  Button,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VerticalCarousel from '../components/VerticalCarousel';

function Landing() {

  const services = [
    {
      title: 'Product Strategy',
      description: 'Craft a clear, scalable product strategy—from vision and roadmap architecture to market positioning—that aligns with your business objectives and accelerates growth.',
    },
    {
      title: 'Hands-on Product Leadership',
      description: 'Provide embedded, execution-focused product leadership to guide teams, establish high-performance practices, and deliver impactful outcomes—not just slide decks.',
    },
    {
      title: 'Innovation & Discovery',
      description: 'Identify high-value opportunities, validate concepts through rapid experimentation, and translate customer insight into AI-native product directions with clear business value.',
    },
    {
      title: 'Process Optimization',
      description: 'Design and implement resilient product and engineering workflows, leveraging modern agile, DevOps, and AI-enabled practices to shorten time-to-market and improve quality.',
    },
  ];

  const carouselContent = [
    {
      title: 'your product vision is unclear?',
      description: 'Agile doesn\'t equal chaos; Does your organisation lack structure and a predictable product development direction suitable for its size?',
    },
    {
      title: 'your team is too slow?',
      description: 'Longer release cycles, unclear bottlenecks, and declining morale are signals of deeper systemic issues. Teams need to be able to deliver value quickly and efficiently.',
    },
    {
      title: 'your team lacks clear product leadership?',
      description: 'Who owns the product direction? Without an unified product leadership, teams lose focus.',
    },
    {
      title: 'you\'re missing market opportunities?',
      description: 'Few things are more frustrating than knowing exactly what customers need but failing to ship on time or at the right level of quality',
    },
  ];

  // Typewriter effect for subtitle
  const subtitleText = 'Hands-on product leadership to architect, build, and scale AI-native products that deliver measurable business impact.';
  const [displayedText, setDisplayedText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  useEffect(() => {
    let currentIndex = 0;
    const typingSpeed = 35; // milliseconds per character

    const typeNextChar = () => {
      if (currentIndex < subtitleText.length) {
        setDisplayedText(subtitleText.slice(0, currentIndex + 1));
        currentIndex++;
        setTimeout(typeNextChar, typingSpeed);
      } else {
        setIsTypingComplete(true);
      }
    };

    // Small delay before starting to type
    const startDelay = setTimeout(() => {
      typeNextChar();
    }, 500);

    return () => clearTimeout(startDelay);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <Header />

      {/* Hero Section with Background Image */}
      <Box
        sx={{
          backgroundImage: 'url(/zurich.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          position: 'relative',
          minHeight: { xs: '700px', md: '100vh' },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1,
          },
        }}
      >
        {/* Hero Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: { xs: 12, md: 16 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 6 } }}>
            {/* Title - Left aligned */}
            <Box sx={{ maxWidth: { xs: '100%', md: '65%' } }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                  color: 'white',
                  textAlign: 'left',
                  lineHeight: 1.1,
                }}
              >
                Transform Your Product Vision into Reality
              </Typography>
            </Box>
            
            {/* Subtitle - Right aligned on desktop with typewriter effect */}
            <Box 
              sx={{ 
                alignSelf: { xs: 'flex-start', md: 'flex-end' },
                maxWidth: { xs: '100%', md: '55%' },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  opacity: 0.95,
                  fontWeight: 300,
                  lineHeight: 1.6,
                  textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                  color: 'white',
                  textAlign: { xs: 'left', md: 'right' },
                  minHeight: { xs: 'auto', md: '4.8em' }, // Reserve space to prevent layout shift
                }}
              >
                {displayedText}
                {!isTypingComplete && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: '2px',
                      height: '1em',
                      bgcolor: 'rgba(148, 163, 184, 0.8)',
                      ml: 0.5,
                      verticalAlign: 'text-bottom',
                      animation: 'blink 0.8s infinite',
                      '@keyframes blink': {
                        '0%, 50%': { opacity: 1 },
                        '51%, 100%': { opacity: 0 },
                      },
                    }}
                  />
                )}
              </Typography>
            </Box>

            {/* CTA - Left aligned */}
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Button
                href="https://calendly.com/actuation/30min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  fontWeight: 600,
                  px: { xs: 2, md: 3 },
                  py: { xs: 0.6, md: 0.75 },
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 252, 248, 0.95)',
                  color: '#1a1a1a',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                Schedule a Call
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ========== EXPERIMENTAL: Vertical Carousel Section ========== */}
      {/* TODO: Remove this section if not needed */}
      <Box
        className="carousel-container"
        sx={{
          bgcolor: '#111827',
          position: 'relative',
        }}
      >
        <VerticalCarousel items={carouselContent} title="Do you feel like..." />
      </Box>
      {/* ========== END EXPERIMENTAL SECTION ========== */}

      {/* Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }} id="services">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            Our Services
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Comprehensive product management solutions tailored to your business needs
          </Typography>
        </Box>

        <Grid container rowSpacing={{ xs: 3, sm: 4, md: 4 }} columnSpacing={{ xs: 2, sm: 3, md: 4 }}>
          {services.map((service, index) => (
            <Grid size={{ xs: 12, sm: 6 }} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  p: { xs: 3, sm: 4 },
                  background: '#ffffff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '6px solid',
                  borderLeftColor: '#1E3A5F',
                  borderRadius: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {service.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                  }}
                >
                  {service.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section - Schedule a Call */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0F172A 0%, #111827 50%, #1E293B 100%)',
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 10, md: 14 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
            borderRadius: '50%',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-10%',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%)',
            borderRadius: '50%',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="overline"
              sx={{
                color: '#7EB5D8',
                fontWeight: 700,
                letterSpacing: 3,
                fontSize: '0.85rem',
                mb: 2,
                display: 'block',
              }}
            >
              Ready to get started?
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                color: 'white',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                lineHeight: 1.2,
              }}
            >
              Let's Build Something
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: 'rgba(186, 230, 253, 0.85)',
                }}
              >
                Extraordinary Together
              </Box>
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 400,
                maxWidth: 550,
                mx: 'auto',
                mb: 5,
                lineHeight: 1.7,
                fontSize: { xs: '1rem', sm: '1.15rem' },
              }}
            >
              Book a free 30-minute discovery session. No pressure, no obligations—just a conversation about how we can help accelerate your product success.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="https://calendly.com/actuation/30min"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                background: 'linear-gradient(90deg, #1E3A5F 0%, #2C5282 100%)',
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                px: { xs: 4, sm: 6 },
                py: { xs: 1.5, sm: 2 },
                borderRadius: '50px',
                textTransform: 'none',
                boxShadow: '0 8px 30px rgba(30, 58, 95, 0.45)',
                border: '1px solid rgba(126, 181, 216, 0.25)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(90deg, #2C5282 0%, #1E3A5F 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 12px 40px rgba(30, 58, 95, 0.55)',
                },
              }}
            >
              Schedule Your Free Call →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
}

export default Landing;